import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { 
  Users, 
  Crown, 
  Calendar, 
  Mail, 
  Trash2, 
  Search,
  Filter,
  ArrowLeft,
  BarChart3,
  TrendingUp,
  UserCheck,
  UserX,
  Settings,
  CreditCard,
  DollarSign,
  PieChart,
  Activity,
  Eye,
  EyeOff,
  RefreshCw,
  Database,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios';

const AdminPanel = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    users: { total: 0, premium: 0, free: 0, premium_percentage: 0 },
    subscriptions: { total: 0, active: 0, cancelled: 0 },
    revenue: { total: 0, monthly: 0 },
    plan_distribution: []
  });
  const [modules, setModules] = useState([]);
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [newModule, setNewModule] = useState({
    name: '',
    description: '',
    version: '',
    type: 'ai',
    status: 'active',
    config: {}
  });

  // Admin yetki kontrolü
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    if (!user.is_admin) {
      toast.error('Bu sayfaya erişim yetkiniz yok!');
      navigate('/');
      return;
    }
    
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchUsers(),
        fetchSubscriptions(),
        fetchPayments(),
        fetchStats(),
        fetchModules()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const backendUrl = process.env.REACT_APP_API_URL || 'https://ai-chat-backend.gokturkgpt.workers.dev';
      const response = await axios.get(`${backendUrl}/api/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Kullanıcılar yüklenemedi');
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const backendUrl = process.env.REACT_APP_API_URL || 'https://ai-chat-backend.gokturkgpt.workers.dev';
      const response = await axios.get(`${backendUrl}/api/admin/subscriptions`);
      setSubscriptions(response.data);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      toast.error('Abonelikler yüklenemedi');
    }
  };

  const fetchPayments = async () => {
    try {
      const backendUrl = process.env.REACT_APP_API_URL || 'https://ai-chat-backend.gokturkgpt.workers.dev';
      const response = await axios.get(`${backendUrl}/api/admin/payments`);
      setPayments(response.data);
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Ödemeler yüklenemedi');
    }
  };

  const fetchStats = async () => {
    try {
      const backendUrl = process.env.REACT_APP_API_URL || 'https://ai-chat-backend.gokturkgpt.workers.dev';
      const response = await axios.get(`${backendUrl}/api/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('İstatistikler yüklenemedi');
    }
  };

  const fetchModules = async () => {
    try {
      const backendUrl = process.env.REACT_APP_API_URL || 'https://ai-chat-backend.gokturkgpt.workers.dev';
      const response = await axios.get(`${backendUrl}/api/admin/modules`);
      setModules(response.data);
    } catch (error) {
      console.error('Error fetching modules:', error);
      // Fallback to mock data
      setModules([
        { id: 1, name: 'GökBörü AI', description: 'Temel AI modeli', version: '1.0.0', type: 'ai', status: 'active', config: { max_tokens: 1000 } },
        { id: 2, name: 'GökBörü AI 2.0', description: 'Gelişmiş AI modeli', version: '2.0.0', type: 'ai', status: 'active', config: { max_tokens: 4000 } },
        { id: 3, name: 'Görsel Üretim', description: 'AI görsel üretim modülü', version: '1.5.0', type: 'image', status: 'active', config: { models: ['dall-e', 'midjourney'] } },
        { id: 4, name: 'Video Üretim', description: 'AI video üretim modülü', version: '1.2.0', type: 'video', status: 'beta', config: { models: ['runway', 'stable-video'] } }
      ]);
    }
  };

  const addModule = async () => {
    try {
      const backendUrl = process.env.REACT_APP_API_URL || 'https://ai-chat-backend.gokturkgpt.workers.dev';
      const response = await axios.post(`${backendUrl}/api/admin/modules`, newModule);
      setModules([...modules, response.data]);
      setShowModuleModal(false);
      setNewModule({ name: '', description: '', version: '', type: 'ai', status: 'active', config: {} });
      toast.success('Modül başarıyla eklendi!');
    } catch (error) {
      console.error('Error adding module:', error);
      toast.error('Modül eklenemedi');
    }
  };

  const updateModule = async (moduleId, updates) => {
    try {
      const backendUrl = process.env.REACT_APP_API_URL || 'https://ai-chat-backend.gokturkgpt.workers.dev';
      const response = await axios.put(`${backendUrl}/api/admin/modules/${moduleId}`, updates);
      setModules(modules.map(m => m.id === moduleId ? response.data : m));
      toast.success('Modül güncellendi!');
    } catch (error) {
      console.error('Error updating module:', error);
      toast.error('Modül güncellenemedi');
    }
  };

  const deleteModule = async (moduleId) => {
    try {
      const backendUrl = process.env.REACT_APP_API_URL || 'https://ai-chat-backend.gokturkgpt.workers.dev';
      await axios.delete(`${backendUrl}/api/admin/modules/${moduleId}`);
      setModules(modules.filter(m => m.id !== moduleId));
      toast.success('Modül silindi!');
    } catch (error) {
      console.error('Error deleting module:', error);
      toast.error('Modül silinemedi');
    }
  };

  const toggleUserPremium = async (userId, isPremium) => {
    try {
      const backendUrl = process.env.REACT_APP_API_URL || 'https://ai-chat-backend.gokturkgpt.workers.dev';
      await axios.post(`${backendUrl}/api/admin/users/${userId}/premium`, {
        action: isPremium ? 'deactivate' : 'activate'
      });
      
      toast.success(`Kullanıcı ${isPremium ? 'premium\'dan çıkarıldı' : 'premium yapıldı'}`);
      fetchUsers();
      fetchStats();
    } catch (error) {
      console.error('Error toggling premium:', error);
      toast.error('Premium durumu değiştirilemedi');
    }
  };

  const cancelSubscription = async (subscriptionId) => {
    if (!window.confirm('Bu aboneliği iptal etmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_API_URL || 'https://ai-chat-backend.gokturkgpt.workers.dev';
      await axios.delete(`${backendUrl}/api/admin/subscriptions/${subscriptionId}`);
      toast.success('Abonelik iptal edildi');
      fetchSubscriptions();
      fetchStats();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error('Abonelik iptal edilemedi');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-green-400 bg-green-400/10';
      case 'cancelled':
        return 'text-red-400 bg-red-400/10';
      case 'completed':
        return 'text-green-400 bg-green-400/10';
      case 'pending':
        return 'text-yellow-400 bg-yellow-400/10';
      case 'failed':
        return 'text-red-400 bg-red-400/10';
      default:
        return 'text-gray-400 bg-gray-400/10';
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filterType === 'all') return matchesSearch;
    if (filterType === 'premium') return matchesSearch && user.is_premium;
    if (filterType === 'free') return matchesSearch && !user.is_premium;
    if (filterType === 'admin') return matchesSearch && user.is_admin;
    
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/')}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
            <span className="bg-red-500 text-white px-2 py-1 rounded text-sm">Premium Yönetimi</span>
          </div>
          <button
            onClick={fetchData}
            className="flex items-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Yenile</span>
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Tabs */}
        <div className="flex space-x-1 mb-8 bg-gray-800 rounded-lg p-1">
          {[
            { id: 'overview', name: 'Genel Bakış', icon: BarChart3 },
            { id: 'users', name: 'Kullanıcılar', icon: Users },
            { id: 'subscriptions', name: 'Abonelikler', icon: Crown },
            { id: 'payments', name: 'Ödemeler', icon: CreditCard },
            { id: 'modules', name: 'Modüller', icon: Settings },
            { id: 'database', name: 'Veritabanı', icon: Database }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all ${
                activeTab === tab.id
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.name}</span>
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gray-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">Toplam Kullanıcı</h3>
                  <Users className="w-8 h-8 text-blue-400" />
                </div>
                <p className="text-3xl font-bold text-white">{stats.users.total}</p>
                <p className="text-sm text-gray-400 mt-1">
                  {stats.users.premium} premium, {stats.users.free} ücretsiz
                </p>
              </div>

              <div className="bg-gray-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">Premium Oranı</h3>
                  <Crown className="w-8 h-8 text-yellow-400" />
                </div>
                <p className="text-3xl font-bold text-white">%{stats.users.premium_percentage}</p>
                <p className="text-sm text-gray-400 mt-1">
                  {stats.users.premium} / {stats.users.total} kullanıcı
                </p>
              </div>

              <div className="bg-gray-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">Toplam Gelir</h3>
                  <DollarSign className="w-8 h-8 text-green-400" />
                </div>
                <p className="text-3xl font-bold text-white">{formatPrice(stats.revenue.total)}</p>
                <p className="text-sm text-gray-400 mt-1">
                  Aylık: {formatPrice(stats.revenue.monthly)}
                </p>
              </div>

              <div className="bg-gray-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">Aktif Abonelik</h3>
                  <Activity className="w-8 h-8 text-purple-400" />
                </div>
                <p className="text-3xl font-bold text-white">{stats.subscriptions.active}</p>
                <p className="text-sm text-gray-400 mt-1">
                  {stats.subscriptions.total} toplam abonelik
                </p>
              </div>
            </div>

            {/* Plan Distribution */}
            <div className="bg-gray-800 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-white mb-6">Plan Dağılımı</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {stats.plan_distribution.map((plan, index) => (
                  <div key={index} className="bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-white font-medium">{plan.plan_name}</h4>
                      <span className="text-2xl font-bold text-blue-400">{plan.count}</span>
                    </div>
                    <div className="w-full bg-gray-600 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ 
                          width: `${(plan.count / stats.subscriptions.active * 100) || 0}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Search and Filter */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Kullanıcı ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-gray-700 text-white pl-10 pr-4 py-2 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                />
              </div>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
              >
                <option value="all">Tümü</option>
                <option value="premium">Premium</option>
                <option value="free">Ücretsiz</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            {/* Users Table */}
            <div className="bg-gray-800 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Kullanıcı</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Durum</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Abonelik</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Toplam Ödeme</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Kayıt Tarihi</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-700/50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                              <span className="text-white font-semibold">
                                {user.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-white">{user.name}</div>
                              <div className="text-sm text-gray-400">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            {user.is_premium ? (
                              <Crown className="w-4 h-4 text-yellow-400" />
                            ) : (
                              <Users className="w-4 h-4 text-gray-400" />
                            )}
                            <span className={`text-sm ${user.is_premium ? 'text-yellow-400' : 'text-gray-400'}`}>
                              {user.is_premium ? 'Premium' : 'Ücretsiz'}
                            </span>
                            {user.is_admin && (
                              <span className="bg-red-500 text-white text-xs px-2 py-1 rounded">Admin</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {user.active_subscription ? (
                            <div>
                              <div className="text-sm text-white">{user.active_subscription.plan_name}</div>
                              <div className="text-xs text-gray-400">
                                {user.active_subscription.billing_cycle === 'yearly' ? 'Yıllık' : 'Aylık'}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          {formatPrice(user.total_payments)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          {formatDate(user.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button
                            onClick={() => toggleUserPremium(user.id, user.is_premium)}
                            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                              user.is_premium
                                ? 'bg-red-500 text-white hover:bg-red-600'
                                : 'bg-green-500 text-white hover:bg-green-600'
                            }`}
                          >
                            {user.is_premium ? 'Premium\'ı Kaldır' : 'Premium Yap'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Subscriptions Tab */}
        {activeTab === 'subscriptions' && (
          <div className="space-y-6">
            <div className="bg-gray-800 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Kullanıcı</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Plan</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Durum</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Faturalandırma</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Bitiş Tarihi</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {subscriptions.map((subscription) => (
                      <tr key={subscription.id} className="hover:bg-gray-700/50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-white">{subscription.user_name}</div>
                            <div className="text-sm text-gray-400">{subscription.user_email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm text-white">{subscription.plan_name}</div>
                            <div className="text-xs text-gray-400">{subscription.plan_description}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(subscription.status)}`}>
                            {subscription.status === 'active' ? 'Aktif' : 
                             subscription.status === 'cancelled' ? 'İptal' : subscription.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          {subscription.billing_cycle === 'yearly' ? 'Yıllık' : 'Aylık'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          {formatDate(subscription.end_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {subscription.status === 'active' && (
                            <button
                              onClick={() => cancelSubscription(subscription.id)}
                              className="px-3 py-1 bg-red-500 text-white rounded text-xs font-medium hover:bg-red-600 transition-colors"
                            >
                              İptal Et
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="space-y-6">
            <div className="bg-gray-800 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Kullanıcı</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Plan</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Tutar</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Durum</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Yöntem</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Tarih</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {payments.map((payment) => (
                      <tr key={payment.id} className="hover:bg-gray-700/50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-white">{payment.user_name}</div>
                            <div className="text-sm text-gray-400">{payment.user_email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          {payment.plan_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white font-medium">
                          {formatPrice(payment.amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(payment.status)}`}>
                            {payment.status === 'completed' ? 'Tamamlandı' :
                             payment.status === 'pending' ? 'Beklemede' :
                             payment.status === 'failed' ? 'Başarısız' : payment.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          {payment.payment_method === 'credit_card' ? 'Kredi Kartı' :
                           payment.payment_method === 'bank_transfer' ? 'Banka Havalesi' :
                           payment.payment_method}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          {formatDate(payment.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Modules Tab */}
        {activeTab === 'modules' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white">Modül Yönetimi</h2>
                <p className="text-gray-400">AI modüllerini ve eklentileri yönetin</p>
              </div>
              <button
                onClick={() => setShowModuleModal(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                <Settings className="w-4 h-4" />
                <span>Yeni Modül Ekle</span>
              </button>
            </div>

            {/* Modules Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {modules.map((module) => (
                <div key={module.id} className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2">{module.name}</h3>
                      <p className="text-gray-400 text-sm mb-2">{module.description}</p>
                      <div className="flex items-center space-x-4 text-sm">
                        <span className="text-blue-400">v{module.version}</span>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          module.status === 'active' ? 'bg-green-500/20 text-green-400' :
                          module.status === 'beta' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {module.status === 'active' ? 'Aktif' :
                           module.status === 'beta' ? 'Beta' : 'Pasif'}
                        </span>
                        <span className="text-gray-500 capitalize">{module.type}</span>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => updateModule(module.id, { status: module.status === 'active' ? 'inactive' : 'active' })}
                        className="p-2 bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 rounded-lg transition-colors"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteModule(module.id)}
                        className="p-2 bg-red-600/20 text-red-400 hover:bg-red-600/30 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Config Display */}
                  {module.config && Object.keys(module.config).length > 0 && (
                    <div className="mt-4 p-3 bg-gray-700/50 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-300 mb-2">Konfigürasyon:</h4>
                      <div className="space-y-1">
                        {Object.entries(module.config).map(([key, value]) => (
                          <div key={key} className="flex justify-between text-sm">
                            <span className="text-gray-400">{key}:</span>
                            <span className="text-white">
                              {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Add Module Modal */}
            {showModuleModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4">
                  <h3 className="text-xl font-bold text-white mb-4">Yeni Modül Ekle</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Modül Adı</label>
                      <input
                        type="text"
                        value={newModule.name}
                        onChange={(e) => setNewModule({...newModule, name: e.target.value})}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="GökBörü AI 3.0"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Açıklama</label>
                      <textarea
                        value={newModule.description}
                        onChange={(e) => setNewModule({...newModule, description: e.target.value})}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows="3"
                        placeholder="Modül açıklaması..."
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Versiyon</label>
                        <input
                          type="text"
                          value={newModule.version}
                          onChange={(e) => setNewModule({...newModule, version: e.target.value})}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="1.0.0"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Tip</label>
                        <select
                          value={newModule.type}
                          onChange={(e) => setNewModule({...newModule, type: e.target.value})}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="ai">AI Modeli</option>
                          <option value="image">Görsel Üretim</option>
                          <option value="video">Video Üretim</option>
                          <option value="text">Metin İşleme</option>
                          <option value="utility">Yardımcı Araç</option>
                        </select>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Durum</label>
                      <select
                        value={newModule.status}
                        onChange={(e) => setNewModule({...newModule, status: e.target.value})}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="active">Aktif</option>
                        <option value="beta">Beta</option>
                        <option value="inactive">Pasif</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="flex space-x-3 mt-6">
                    <button
                      onClick={addModule}
                      className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Modül Ekle
                    </button>
                    <button
                      onClick={() => setShowModuleModal(false)}
                      className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      İptal
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Database Tab */}
        {activeTab === 'database' && (
          <div className="space-y-6">
            <div className="text-center">
              <Database className="w-16 h-16 text-blue-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Veritabanı Yönetimi</h2>
              <p className="text-gray-400 mb-6">
                Kullanıcı verilerini ve veri saklama politikalarını yönetin
              </p>
              <button
                onClick={() => navigate('/database')}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center space-x-2 mx-auto"
              >
                <Database className="w-5 h-5" />
                <span>Veritabanı Yönetim Paneli</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;